<template>
  <div class="mobile-page">
    <!-- Theme toggle on the left -->
    <div class="theme-toggle-left">
      <div class="theme-switch" :class="{ active: isDark }" @click="toggleTheme">
        <div class="theme-switch-track">
          <div class="theme-switch-thumb"></div>
        </div>
      </div>
    </div>

    <!-- Language dropdown in top right corner -->
    <div class="page-controls">
      <div class="lang-dropdown" :class="{ open: isLangDropdownOpen }">
        <button class="lang-btn" @click="toggleLangDropdown">
          {{ currentLang.toUpperCase() }}
        </button>
        <div class="lang-list">
          <button 
            v-for="locale in availableLocales" 
            :key="locale.code"
            class="lang-list-btn"
            :class="{ active: locale.code === currentLang }"
            @click="changeLanguage(locale.code)"
          >
            {{ locale.code.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>

    <div class="main-container">
      <div class="center-content">
        <div class="mobile-card">
          <!-- Header inside card -->
          <div class="card-header">
            <div class="card-title">{{ $t('locker.title') }}</div>
          </div>
          
          <div class="card-content">
            <div class="download-slogan">
              {{ $t('locker.slogan') }}
            </div>
            <div class="locker-illustration">
              <img 
                :src="isDark ? require('@/assets/images/locker-illustration-dark.png') : require('@/assets/images/locker-illustration.png')" 
                alt="Locker Illustration"
              >
            </div>
            <div class="download-block">
              <h2 class="download-title">{{ $t('locker.downloadTitle') }}</h2>
              <div class="download-btns">
                <a 
                  href="https://apps.apple.com/sg/app/community-locker/id6743063611" 
                  class="store-button" 
                  :class="{ hidden: hideAppStore }"
                  target="_blank" 
                  rel="noopener"
                >
                  <i class="fab fa-apple store-icon"></i>
                  <span>{{ $t('locker.appStore') }}</span>
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=io.lockerbot.communityLocker" 
                  class="store-button" 
                  :class="{ hidden: hideGooglePlay }"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="fab fa-google-play store-icon"></i>
                  <span>{{ $t('locker.googlePlay') }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Logo and Copyright -->
        <LogoFooter />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LogoFooter from '@/components/LogoFooter.vue'
import '@/assets/styles/mobile-pages.css'

const { t, locale } = useI18n()
const route = useRoute()

const isLangDropdownOpen = ref(false)
const hideAppStore = ref(false)
const hideGooglePlay = ref(false)
const isDark = ref(false)

const currentLang = computed(() => locale.value)

const availableLocales = [
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'en', name: 'English' },
  { code: 'ko', name: '한국어' },
  { code: 'ru', name: 'Русский' }
]

const toggleLangDropdown = () => {
  isLangDropdownOpen.value = !isLangDropdownOpen.value
}

const changeLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('locale', lang)
  isLangDropdownOpen.value = false
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
  updateTheme()
}

const updateTheme = () => {
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    document.documentElement.classList.remove('light')
  } else {
    document.documentElement.classList.add('light')
    document.documentElement.classList.remove('dark')
  }
}

const detectOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  
  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    hideGooglePlay.value = true
    hideAppStore.value = false
  }
  // Android detection
  else if (/android/i.test(userAgent)) {
    hideAppStore.value = true
    hideGooglePlay.value = false
  }
  // Desktop or other - show both buttons
  else {
    hideAppStore.value = false
    hideGooglePlay.value = false
  }
}

const handleClickOutside = (event) => {
  if (!event.target.closest('.lang-dropdown')) {
    isLangDropdownOpen.value = false
  }
}

onMounted(() => {
  detectOS()
  document.addEventListener('click', handleClickOutside)
  
  // Set initial language from localStorage or detect browser language
  const savedLang = localStorage.getItem('locale')
  if (savedLang && availableLocales.find(l => l.code === savedLang)) {
    locale.value = savedLang
  } else {
    const browserLang = navigator.language || navigator.userLanguage
    const langCode = browserLang.split('-')[0].toLowerCase()
    if (availableLocales.find(l => l.code === langCode)) {
      locale.value = langCode
    }
  }

  // Set initial theme from localStorage or detect system preference
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    isDark.value = savedTheme === 'dark'
  } else {
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  updateTheme()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* LockerPage specific styles - only unique styles that don't exist in mobile-pages.css */
.download-block {
  margin-top: 24px;
}
</style> 